import {
  Box,
  Heading,
  HStack,
  Text,
  VStack,
  Drawer,
  IconButton,
} from '@chakra-ui/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  HiMenu,
  HiX,
  HiHome,
  HiTemplate,
  HiCode,
  HiCog,
  HiClipboardList,
  HiCloudUpload,
  HiDocumentText,
  HiLockClosed,
  HiPlay,
  HiBriefcase,
  HiChevronDown,
  HiChevronRight,
  HiSparkles,
} from 'react-icons/hi';
import { useLocation, Link, useNavigate } from 'react-router-dom';

import {
  useAppConfig,
  RECENTLY_OPENED_SIDEBAR_COUNT,
} from '#providers/app-config-provider';
import { useSidebar } from '#providers/sidebar-provider';
import { getBuildInfo, type BuildInfo } from '#utils/build-info';
import { formatDocumentName } from '#utils/format-document-name';

const mainNavItems = [
  { path: '/', label: 'Dashboard', icon: HiHome },
  { path: '/documents', label: 'Documents', icon: HiDocumentText },
  { path: '/agents', label: 'Agents', icon: HiSparkles },
  { path: '/secrets', label: 'Secrets', icon: HiLockClosed },
] as const;

const advancedNavItems = [
  { path: '/uploads', label: 'Uploads', icon: HiCloudUpload },
  { path: '/processors', label: 'Processors', icon: HiCode },
  { path: '/instance', label: 'Processor instances', icon: HiPlay },
  { path: '/prompts', label: 'Saved Prompts', icon: HiTemplate },
  { path: '/ai-audit', label: 'AI audit log', icon: HiClipboardList },
  {
    path: '/workspace/settings',
    label: 'Workspace Settings',
    icon: HiBriefcase,
  },
] as const;

function pathnameMatchesAdvancedNav(pathname: string) {
  if (pathname.startsWith('/uploads')) return true;

  if (pathname.startsWith('/processors')) return true;

  if (pathname.startsWith('/instance')) return true;

  if (pathname === '/prompts' || pathname.startsWith('/prompts/')) return true;

  if (pathname.startsWith('/ai-audit')) return true;

  if (pathname.startsWith('/workspace/settings')) return true;

  return false;
}

interface SidebarDrawerProps {
  hideFixedToggle?: boolean;
}

export function SidebarDrawer({ hideFixedToggle = false }: SidebarDrawerProps) {
  const { open, onOpen, onClose } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useAppConfig();
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const prevPathnameRef = useRef(location.pathname);

  const recentDocs = (config.recentlyOpenedDocuments ?? []).slice(
    0,
    RECENTLY_OPENED_SIDEBAR_COUNT,
  );

  useEffect(() => {
    getBuildInfo().then(setBuildInfo);
  }, []);

  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (pathnameMatchesAdvancedNav(location.pathname)) {
      setShowAdvanced(true);
    } else if (
      pathnameMatchesAdvancedNav(prev) &&
      !pathnameMatchesAdvancedNav(location.pathname)
    ) {
      setShowAdvanced(false);
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;

    return location.pathname.startsWith(path) && path !== '/';
  };

  const settingsActive = isActive('/settings');

  const pos = config.sidebarTogglePosition;
  const placement =
    pos === 'top-left' || pos === 'bottom-left' ? 'start' : 'end';
  const togglePos = {
    top: pos.startsWith('top') ? 6 : undefined,
    bottom: pos.startsWith('bottom') ? 6 : undefined,
    left: pos.endsWith('left') ? 6 : undefined,
    right: pos.endsWith('right') ? 6 : undefined,
  };

  return (
    <>
      <Drawer.Root
        open={open}
        onOpenChange={(e) => {
          if ('open' in e && !e.open) {
            onClose();
          }
        }}
        placement={placement}
        size="sm"
      >
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg="var(--background-base-color)">
            <Drawer.Header
              display="flex"
              alignItems="center"
              gap={2}
              justifyContent="space-between"
            >
              <Heading size="md" color="blue.600" flexShrink={0}>
                Note Plan
              </Heading>
              <HStack gap={0} flexShrink={0}>
                <IconButton
                  aria-label="Settings"
                  variant="ghost"
                  size="sm"
                  color={settingsActive ? 'blue.600' : undefined}
                  onClick={() => {
                    navigate('/settings');
                    onClose();
                  }}
                >
                  <HiCog />
                </IconButton>
                <IconButton
                  aria-label={
                    showAdvanced
                      ? 'Show main navigation'
                      : 'Show advanced navigation'
                  }
                  aria-pressed={showAdvanced}
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  {showAdvanced ? <HiChevronDown /> : <HiChevronRight />}
                </IconButton>
                <IconButton
                  aria-label="Close menu"
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                >
                  <HiX />
                </IconButton>
              </HStack>
            </Drawer.Header>
            <Drawer.Body pt={0}>
              <VStack gap={1} align="stretch">
                {showAdvanced ? (
                  advancedNavItems.map(({ path, label, icon: Icon }) => {
                    const active = isActive(path);
                    return (
                      <Link
                        key={path}
                        to={path}
                        onClick={onClose}
                        style={{ textDecoration: 'none' }}
                      >
                        <HStack
                          gap={3}
                          px={4}
                          py={3}
                          borderRadius="md"
                          bg={active ? 'blue.50' : 'transparent'}
                          borderWidth="1px"
                          borderColor={active ? 'blue.200' : 'transparent'}
                          _hover={{
                            bg: active ? 'blue.100' : 'bg.muted',
                          }}
                          _active={{
                            bg: active ? 'blue.200' : 'bg.emphasized',
                          }}
                        >
                          <Box
                            color={active ? 'blue.600' : 'fg.muted'}
                            fontSize="lg"
                          >
                            <Icon />
                          </Box>
                          <Text
                            fontWeight={active ? 'semibold' : 'normal'}
                            color={active ? 'blue.700' : 'fg'}
                            fontSize="md"
                          >
                            {label}
                          </Text>
                        </HStack>
                      </Link>
                    );
                  })
                ) : (
                  <Fragment>
                    {mainNavItems.map(({ path, label, icon: Icon }) => {
                      const active = isActive(path);
                      return (
                        <Link
                          key={path}
                          to={path}
                          onClick={onClose}
                          style={{ textDecoration: 'none' }}
                        >
                          <HStack
                            gap={3}
                            px={4}
                            py={3}
                            borderRadius="md"
                            bg={active ? 'blue.50' : 'transparent'}
                            borderWidth="1px"
                            borderColor={active ? 'blue.200' : 'transparent'}
                            _hover={{
                              bg: active ? 'blue.100' : 'bg.muted',
                            }}
                            _active={{
                              bg: active ? 'blue.200' : 'bg.emphasized',
                            }}
                          >
                            <Box
                              color={active ? 'blue.600' : 'fg.muted'}
                              fontSize="lg"
                            >
                              <Icon />
                            </Box>
                            <Text
                              fontWeight={active ? 'semibold' : 'normal'}
                              color={active ? 'blue.700' : 'fg'}
                              fontSize="md"
                            >
                              {label}
                            </Text>
                          </HStack>
                        </Link>
                      );
                    })}
                    {recentDocs.length > 0 && (
                      <Box pl={4} pr={4}>
                        <Text
                          pt={2}
                          pb={1}
                          fontSize="xs"
                          fontWeight="semibold"
                          color="fg.muted"
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Recent
                        </Text>
                        {recentDocs.map((doc) => {
                          const docHref = `/documents/${doc.path}`;
                          const label = formatDocumentName(doc.path).join(
                            ' > ',
                          );
                          return (
                            <Link
                              key={doc.id}
                              to={docHref}
                              onClick={onClose}
                              style={{ textDecoration: 'none' }}
                            >
                              <HStack
                                gap={3}
                                py={2}
                                borderRadius="md"
                                _hover={{ bg: 'bg.muted' }}
                                _active={{ bg: 'bg.emphasized' }}
                              >
                                <Text
                                  fontWeight="normal"
                                  color="fg"
                                  fontSize="sm"
                                  lineClamp={1}
                                >
                                  {label}
                                </Text>
                              </HStack>
                            </Link>
                          );
                        })}
                      </Box>
                    )}
                  </Fragment>
                )}
              </VStack>
            </Drawer.Body>
            {buildInfo && (
              <Drawer.Footer borderTopWidth="1px" borderColor="border">
                <VStack gap={1} align="stretch" width="100%">
                  <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                    Commit: {buildInfo.commit.substring(0, 7)}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    Built: {new Date(buildInfo.buildTime).toLocaleString()}
                  </Text>
                </VStack>
              </Drawer.Footer>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      {!hideFixedToggle && (
        <Box position="fixed" zIndex={1000} {...togglePos}>
          <IconButton
            aria-label="Open navigation menu"
            onClick={onOpen}
            colorScheme="blue"
            variant="outline"
            bg="bg"
          >
            <HiMenu />
          </IconButton>
        </Box>
      )}
    </>
  );
}
